import { useInfiniteQuery, useQuery } from "@tanstack/react-query"
import dayjs from "dayjs"
import { GalleryAdd, Send2 } from "iconsax-reactjs"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { toast } from "sonner"

import { ChatEmpty, GoBack, LoadingSpinner } from "@/components/shared"
import { Avatar, AvatarFallback, AvatarImage, Button, Image, Input } from "@/components/ui"
import { fetchSubmissionDetails } from "@/features/jobs/manage/api/manage.api"
import { SUBMISSION_STATUS_CONTENT } from "@/features/jobs/manage/constants"
import {
  createMessagePresignedUrls,
  getAllMessagesByCode,
  getConversationsById
} from "@/features/users/api/chat.api"
import { EFolderAssets } from "@/modules/helpers/enum"
import { useCurrentUser } from "@/modules/helpers/hooks/shared"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import useWSChat from "@/modules/helpers/hooks/use-ws-chat"
import { getImageByNameFromS3 } from "@/modules/helpers/utils"
// import { uploadFilesWithApiUrls } from "@/modules/helpers/utils/aws-s3"
import { Message } from "@/modules/types"

// --- Styles: Tailwind + shadcn (use this file inside your pages/components folder)

type FormValues = {
  message: string
}

interface InboxProps {
  code?: string
  shopName?: string
  otherUserId?: string
  submissionId?: string
}

// Extracted component for message content rendering
const MessageContent = ({ message }: { message: Message }) => {
  const attachments = Array.isArray(message.attachmentUrl)
    ? message.attachmentUrl
    : message.attachmentUrl
      ? [message.attachmentUrl]
      : []

  if (attachments.length > 0) {
    return (
      <div className="flex flex-wrap gap-2 max-w-50">
        {attachments.map((url, idx) => (
          <div key={idx} className="rounded-lg overflow-hidden w-full">
            {message.type === "video" ? (
              <video
                src={getImageByNameFromS3(url)}
                controls
                className="w-full h-auto object-cover"
              />
            ) : (
              <Image
                src={getImageByNameFromS3(url)}
                alt={`message-image-${idx}`}
                className="w-full h-auto object-cover"
              />
            )}
          </div>
        ))}
      </div>
    )
  }

  if (message.type === "video") {
    return (
      <div className="rounded-lg overflow-hidden max-w-50">
        <video
          src={getImageByNameFromS3(message.content)}
          controls
          className="w-full h-auto object-cover"
        />
      </div>
    )
  }

  if (
    message.type === "image" ||
    (!message.type && message.content?.startsWith("http") && !message.content?.includes(" "))
  ) {
    return (
      <div className="rounded-lg overflow-hidden max-w-50">
        <Image
          src={getImageByNameFromS3(message.content)}
          alt="message-image"
          className="w-full h-auto object-cover"
        />
      </div>
    )
  }

  return <div className="break-words">{message.content}</div>
}

export default function Inbox({ code, shopName, otherUserId, submissionId }: InboxProps) {
  const { user } = useCurrentUser()

  const isValidCode = !!code && code !== "undefined" && code.length > 0

  const [isActive, setIsActive] = useState(false)

  const [isUploading, setIsUploading] = useState(false)
  const listRef = useRef<HTMLDivElement | null>(null)
  const bottomRef = useRef<HTMLDivElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const { data: conversation } = useQuery({
    queryKey: ["conversation_", code],
    queryFn: () => getConversationsById(code!),
    enabled: isValidCode
  })

  const { data: submissionDetails, isLoading: isSubmissionLoading } = useQuery({
    queryKey: ["submission_detail_", submissionId],
    queryFn: () => fetchSubmissionDetails(submissionId!),
    enabled: !!submissionId
  })

  const {
    data: dataMessage,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading
  } = useInfiniteQuery({
    queryKey: ["messages_", code],
    queryFn: ({ pageParam = 1 }) => getAllMessagesByCode(code, Number(pageParam)),
    enabled: isValidCode,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const page = lastPage?.data?.data?.page || 1
      const take = lastPage?.data?.data?.take || 10
      const total = lastPage?.data?.data?.total || 0
      return page * take >= total ? undefined : page + 1
    },
    select: (data) => {
      const list = data.pages.reduce((acc: Message[], cur: any) => {
        return [...acc, ...(cur?.data?.data?.list ?? [])]
      }, [] as Message[])
      const lastPage = data.pages[data.pages.length - 1]
      return {
        list,
        total: lastPage?.data?.data?.total,
        page: lastPage?.data?.data?.page
      }
    }
  })

  const form = useForm<FormValues>({ defaultValues: { message: "" } })

  // useWSChat should work in web too. It returns messages array and helpers.
  const { refSocket, messages, updateMessages } = useWSChat({
    data: dataMessage?.list || [],
    room: code || "",
    onSwitch: () => setIsActive(true)
  })

  useEffect(() => {
    const status = conversation?.data?.data?.status
    setIsActive(status === "ACTIVE")
  }, [conversation?.data?.data?.status])

  // scroll to bottom when messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "auto" })
  }, [messages?.length])

  const onSendMessage = (vals: FormValues) => {
    const message = vals.message?.trim()
    if (!message) return
    const messageSend: Message = {
      content: message,
      senderId: user?.id,
      isNew: true,
      createdAt: new Date().toISOString()
    } as Message

    // emit via websocket
    if (refSocket?.current) {
      refSocket.current.emit("sendMessage", {
        content: message,
        conversationCode: code,
        otherUserId
      })
    }

    updateMessages(messageSend)
    form.reset()
  }

  const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    if (!code) return

    const fileList = Array.from(files)

    // Validate file types - only allow images
    const invalidTypeFiles = fileList.filter((file) => !file.type.startsWith("image/"))
    if (invalidTypeFiles.length > 0) {
      toast.error("Chỉ cho phép upload ảnh")
      if (fileInputRef.current) fileInputRef.current.value = ""
      return
    }

    // Validate file sizes
    const oversizedFiles = fileList.filter((file) => file.size > MAX_FILE_SIZE)
    if (oversizedFiles.length > 0) {
      toast.error("Kích thước file không được vượt quá 5MB")
      if (fileInputRef.current) fileInputRef.current.value = ""
      return
    }

    try {
      setIsUploading(true)

      const res = await createMessagePresignedUrls(
        code,
        fileList.map((f) => f.name)
      )
      const presignedUrls = res.data.data

      if (!presignedUrls || presignedUrls.length !== fileList.length) {
        toast.error("Lỗi tạo link upload")
        return
      }

      const results = await Promise.all(
        fileList.map(async (file, idx) => {
          const { uploadUrl, fileUrl } = presignedUrls[idx]
          await fetch(uploadUrl, { method: "PUT", body: file })

          return { fileUrl, type: "image" as const, idx }
        })
      )

      // Emit and update in order with delay to ensure backend preserves order
      for (const { fileUrl, type, idx } of results) {
        // Emit via socket directly, no API call
        if (refSocket?.current) {
          refSocket.current.emit("sendMessage", {
            content: "", // Content should be empty for file messages
            conversationCode: code,
            otherUserId,
            type,
            attachmentUrl: [fileUrl]
          })
        }

        const messageSend: Message = {
          id: `${Date.now()}_${idx}`,
          content: "",
          senderId: user?.id,
          isNew: true,
          createdAt: new Date(Date.now() + idx * 1000).toISOString(), // Increment time to preserve order locally
          type: type,
          attachmentUrl: [fileUrl]
        } as Message
        updateMessages(messageSend)

        // Small delay to ensure backend timestamp/ID distinction
        await new Promise((resolve) => setTimeout(resolve, 300))
      }
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }
  }

  // infinite scroll: load more when user scrolls to top
  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    if (el.scrollTop === 0 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }

  // Get other user from conversation members
  const otherUser = conversation?.data?.data?.members?.find(
    (member) => member.userId !== user?.id
  )?.user

  const submissionData = submissionDetails?.data?.data
  const product = submissionData?.affCampaigns?.products
  const productTitle = product?.title || submissionData?.campaigns?.title || ""
  const productBrand =
    submissionData?.campaigns?.shopName || submissionData?.affCampaigns?.shopName || shopName || ""
  const productImage = product?.images?.[0]
  const statusText = submissionData?.status && SUBMISSION_STATUS_CONTENT[submissionData?.status]
  const statusDate = submissionData?.workingAt
    ? dayjs(submissionData.workingAt).format("DD/MM/YYYY")
    : submissionData?.createdAt
      ? dayjs(submissionData.createdAt).format("DD/MM/YYYY")
      : undefined

  // Sort messages by createdAt ASC (and id for stability)
  const sortedMessages = useMemo(() => {
    if (!messages) return []
    return [...messages].sort((a, b) => {
      const timeA = new Date(a.createdAt || 0).getTime()
      const timeB = new Date(b.createdAt || 0).getTime()
      if (timeA !== timeB) return timeA - timeB
      // If same time, sort by id (assuming number or string)
      if (a.id && b.id) {
        return a.id > b.id ? 1 : -1
      }
      return 0
    })
  }, [messages])

  return (
    <div className="h-screen bg-white flex flex-col w-full">
      {/* Header */}
      <div className="sticky top-0 z-10 w-full bg-white">
        <GoBack title="Tin nhắn" />
        {submissionId && (
          <div className="px-4 pt-3 pb-2 border-b border-gray-200">
            {isSubmissionLoading ? (
              <div className="flex gap-3 animate-pulse">
                <div className="w-20 h-20 bg-gray-200 rounded-xl" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                  <div className="h-6 bg-gray-100 rounded w-1/3 self-end" />
                </div>
              </div>
            ) : (
              submissionData && (
                <div className="flex gap-3">
                  {productImage && (
                    <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                      <Image
                        src={productImage}
                        alt={productTitle}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0 flex">
                    <div className="flex-1 min-w-0">
                      {productTitle && (
                        <h3 className="text-sm font-bold text-black mb-1 line-clamp-2">
                          {productTitle}
                        </h3>
                      )}
                      <p className="text-xs text-primary mb-2">{productBrand}</p>
                    </div>
                    {(statusText || statusDate) && (
                      <div className="flex flex-col items-end gap-1">
                        {statusText && (
                          <div className="bg-orange-100 border border-orange-700 text-orange-700 px-3 py-1 rounded-full text-[10px] font-medium">
                            {statusText}
                          </div>
                        )}
                        {statusDate && (
                          <p className="text-[10px] text-yellow-600 font-medium">{statusDate}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>

      <main className="flex-1 overflow-hidden bg-white flex flex-col">
        {/* Product Info Card */}

        {/* Messages Container */}
        <div
          className="flex-1 overflow-auto p-4 flex flex-col-reverse gap-3"
          onScroll={onScroll}
          ref={listRef}
        >
          {/* bottom sentinel */}
          <div ref={bottomRef} />

          {isLoading && (
            <div className="w-full flex items-center justify-center py-20">
              <LoadingSpinner className="w-20 h-20" />
            </div>
          )}

          {!isLoading && (!messages || messages.length === 0) && (
            <div className="flex flex-col items-center justify-center gap-2 flex-1">
              <ChatEmpty />
              <p className="text-lg font-semibold text-primary-800">Chưa có lịch sử Chat!</p>
              <p className="text-xs text-gray-500">Bắt đầu trao đổi cùng nhãn hàng nào!</p>
            </div>
          )}

          {sortedMessages.length > 0 && (
            <>
              {[...sortedMessages].reverse().map((m: Message, idx: number) => {
                const isMine = m.senderId === user?.id

                // For sender avatar: use m.sender if available, otherwise fall back to otherUser from conversation
                const senderAvatarName = m.sender?.avatarName || otherUser?.avatarName
                const senderAvatarId = m.sender?.id || otherUser?.id
                const senderAvatar = getImageByNameFromS3(
                  senderAvatarName,
                  senderAvatarId,
                  EFolderAssets.AVATAR
                )

                const myAvatar = getImageByNameFromS3(
                  user?.avatarName,
                  user?.id,
                  EFolderAssets.AVATAR
                )
                const otherFallback = otherUser?.profiles?.fullName?.[0] || "U"
                const myFallback =
                  user?.profiles?.fullName?.slice(0, 2)?.toUpperCase() || otherFallback || "ZZP"

                const messageTimeRaw = m.createdAt || m.timeStamp
                const messageTime = messageTimeRaw
                  ? dayjs(messageTimeRaw).format("HH:mm DD/MM")
                  : ""

                return (
                  <div
                    key={`${m.id || idx}`}
                    className={cn("flex items-end gap-2", isMine ? "justify-end" : "justify-start")}
                  >
                    {!isMine && (
                      <Avatar className="w-9 h-9 flex-shrink-0 border border-white shadow">
                        {senderAvatar ? (
                          <AvatarImage src={senderAvatar} alt="" />
                        ) : (
                          <AvatarFallback className="bg-gray-200 text-gray-600 text-xs font-semibold">
                            {otherFallback}
                          </AvatarFallback>
                        )}
                      </Avatar>
                    )}

                    <div
                      className={cn(
                        "max-w-[80%] rounded-2xl px-3 py-2 shadow-sm border text-sm leading-relaxed text-white",
                        isMine
                          ? "bg-[#3BC280] border-[#30B372] rounded-br-none"
                          : "bg-[#0B8699] border-[#087384] rounded-bl-none"
                      )}
                    >
                      <MessageContent message={m} />
                      {messageTime && (
                        <div className="mt-1 flex justify-end text-xss opacity-80">
                          <span className="shrink-0">{messageTime}</span>
                        </div>
                      )}
                    </div>

                    {isMine && (
                      <Avatar className="w-9 h-9 flex-shrink-0 border border-white shadow">
                        {myAvatar ? (
                          <AvatarImage src={myAvatar} alt="" />
                        ) : (
                          <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                            {myFallback}
                          </AvatarFallback>
                        )}
                      </Avatar>
                    )}
                  </div>
                )
              })}
            </>
          )}

          {isFetchingNextPage && (
            <div className="w-full flex items-center justify-center py-4">
              <LoadingSpinner className="w-20 h-20" />
            </div>
          )}
        </div>
      </main>

      {/* Input Bar */}
      <form
        onSubmit={form.handleSubmit(onSendMessage)}
        className="bg-white p-3 border-t flex gap-2 items-center sticky bottom-0"
      >
        <div className="relative flex-1">
          <Controller
            name="message"
            control={form.control}
            render={({ field }) => (
              <Input
                {...field}
                placeholder={isActive ? "Nhập vào tin nhắn" : "Bắt đầu trao đổi cùng nhãn hàng nào"}
                disabled={!isActive}
                className={cn("w-full rounded-2xl pr-12", {
                  "!opacity-50 bg-gray-200": !isActive
                })}
              />
            )}
          />

          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-2 text-gray-600 hover:text-gray-800"
              disabled={!isActive || isUploading}
            >
              {isUploading ? (
                <LoadingSpinner className="w-6 h-6 text-gray-500" />
              ) : (
                <GalleryAdd size={24} />
              )}
            </button>
          </div>
        </div>

        <Controller
          name="message"
          control={form.control}
          render={({ field }) => (
            <Button
              type="submit"
              disabled={!field.value || !isActive}
              className="rounded-full w-10 h-10 p-0 bg-primary hover:bg-primary/90"
            >
              <Send2 size={18} color="#fff" />
            </Button>
          )}
        />

        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="image/*"
          multiple
          onChange={handleFileUpload}
        />
      </form>
    </div>
  )
}
